import { Coins, Activity, DollarSign } from 'lucide-react'
import { useDashboardBalances, useConversationCredits } from '@/lib/api/hooks'
import { McpStatus } from './McpStatus'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface UsageCounterProps {
  conversationTitle?: string
  conversationUuid?: string | null
  className?: string
}

export function UsageCounter({
  conversationTitle,
  conversationUuid,
  className = '',
}: UsageCounterProps) {
  const { data: balances } = useDashboardBalances()
  const { data: convCredits } = useConversationCredits(conversationUuid ?? null)

  const creditBalance = balances?.tool_calls?.total_available ?? 0
  const totalUsed = convCredits?.total_credits ?? 0
  const breakdown = convCredits?.breakdown ?? {}
  const totalCost = convCredits?.total_cost

  const toolCredits = (breakdown.mcp_tool?.credits ?? 0) + (breakdown.playground_tool?.credits ?? 0)
  const toolCount = (breakdown.mcp_tool?.count ?? 0) + (breakdown.playground_tool?.count ?? 0)
  const msgCredits = breakdown.playground_message?.credits ?? 0
  const msgCount = breakdown.playground_message?.count ?? 0
  const extCredits = breakdown.external_mcp?.credits ?? 0
  const extCount = breakdown.external_mcp?.count ?? 0

  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      {/* Left: Conversation Title */}
      {conversationTitle && (
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-medium truncate hidden sm:block">{conversationTitle}</h2>
          <h2 className="text-xs font-medium truncate sm:hidden">{conversationTitle}</h2>
        </div>
      )}

      {/* Right: Usage Stats */}
      <div className="flex items-center gap-2 sm:gap-3 text-[11px] flex-shrink-0">
        {/* MCP Connection Status */}
        <McpStatus />

        {/* Divider */}
        <div className="h-3 w-px bg-border hidden sm:block" />

        {/* Credits used in this conversation */}
        {totalUsed > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 cursor-help">
                <Activity className="w-3 h-3 text-muted-foreground" />
                <span className="font-mono font-medium">{totalUsed}</span>
                <span className="text-muted-foreground hidden md:inline">
                  credit{totalUsed !== 1 ? 's' : ''} used
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="font-medium mb-1">Credits Used in Conversation</p>
              <div className="text-xs opacity-90 space-y-0.5">
                {toolCount > 0 && (
                  <p>
                    {toolCount} tool call{toolCount !== 1 ? 's' : ''} — {toolCredits} credit
                    {toolCredits !== 1 ? 's' : ''}
                  </p>
                )}
                {msgCount > 0 && (
                  <p>
                    {msgCount} message{msgCount !== 1 ? 's' : ''} — {msgCredits} credit
                    {msgCredits !== 1 ? 's' : ''}
                  </p>
                )}
                {extCount > 0 && (
                  <p>
                    {extCount} external — {extCredits} credit{extCredits !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Credit balance */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <Coins
                className={`w-3 h-3 ${creditBalance <= 10 ? 'text-warning' : 'text-muted-foreground'}`}
              />
              <span className="text-muted-foreground hidden sm:inline">Balance:</span>
              <span
                className={`font-mono font-medium ${creditBalance <= 10 ? 'text-warning' : ''}`}
              >
                {creditBalance}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="font-medium mb-1">Credit Balance</p>
            <p className="text-xs opacity-90">{creditBalance} credits remaining</p>
          </TooltipContent>
        </Tooltip>

        {/* Conversation provider cost (visible only when backend includes it) */}
        {totalCost != null && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 cursor-help">
                <DollarSign className="w-3 h-3 text-muted-foreground" />
                <span className="font-mono font-medium">${totalCost.toFixed(4)}</span>
                <span className="text-muted-foreground hidden md:inline">cost</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="font-medium mb-1">Conversation Cost</p>
              <p className="text-xs opacity-90">Provider cost for this conversation (USD).</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
